import { type Application, Graphics } from 'pixi.js';
import { type System, ComponentStore, type World } from '@ecs/world';
import { DEFAULT_TUNING, type Tuning } from '@game/tuning';
import type {
	AmmoComponent,
	EnemyComponent,
	KinematicComponent,
	PlatformComponent,
	ProjectileComponent,
	TimeScaleResource,
	TransformComponent,
	VelocityComponent
} from '@game/scenes/characterPrototype/types';
import { clamp } from '@game/scenes/characterPrototype/math';
import { circleIntersectsRect, circleRectClosestPoint } from './geometry2d';

export type SpawnPlatformFn = (
	world: World,
	platforms: ComponentStore<PlatformComponent>,
	graphics: ComponentStore<Graphics>,
	stage: Application['stage'],
	x: number,
	y: number,
	width: number,
	height: number
) => number;

export type SpawnExplosionFn = (
	world: World,
	transforms: ComponentStore<TransformComponent>,
	graphics: ComponentStore<Graphics>,
	stage: Application['stage'],
	x: number,
	y: number,
	tuning: Tuning
) => void;

export type ApplyPlayerDamageFn = (world: World, amount: number) => void;

export type ProjectileSystemOptions = {
	spawnPlatform: SpawnPlatformFn;
	spawnExplosion: SpawnExplosionFn;
	applyPlayerDamage: ApplyPlayerDamageFn;
};

export function createProjectileSystem(
	projectiles: ComponentStore<ProjectileComponent>,
	transforms: ComponentStore<TransformComponent>,
	velocities: ComponentStore<VelocityComponent>,
	kinematics: ComponentStore<KinematicComponent>,
	graphics: ComponentStore<Graphics>,
	enemies: ComponentStore<EnemyComponent>,
	platforms: ComponentStore<PlatformComponent>,
	ammos: ComponentStore<AmmoComponent>,
	ammoGraphics: ComponentStore<Graphics>,
	options: ProjectileSystemOptions
): System {
	return ({ delta, world }) => {
		const tuning = world.getResource<Tuning>('tuning') ?? DEFAULT_TUNING;
		const timescale = world.getResource<TimeScaleResource>('timescale') ?? { value: 1 } as TimeScaleResource;
		const dt = delta * timescale.value;
		const playerId = world.getResource<number>('player-entity');
		const playerTransform = playerId ? transforms.get(playerId) : null;
		const playerKinematic = playerId ? kinematics.get(playerId) : null;
		const stage = world.getResource<Application['stage']>('stage');
		const toRemove: number[] = [];
		const platformSplits: Array<{ platformEntity: number; impactX: number; impactY: number }> = [];

		projectiles.forEach((projectile, entity) => {
			const transform = transforms.get(entity);
			const velocity = velocities.get(entity);
			if (!transform || !velocity) {
				toRemove.push(entity);
				return;
			}
			projectile.lifetime -= dt;
			transform.x += velocity.x * dt;
			transform.y += velocity.y * dt;

			// collide with platforms (both player & enemy projectiles)
			const radius = projectile.size * 0.5;
			let hitPlatformEntity: number | null = null;
			let hitImpactX = 0;
			let hitImpactY = 0;
			let hasImpact = false;
			platforms.forEach((platform, platformEntity) => {
				if (hitPlatformEntity !== null) return;
				if (
					circleIntersectsRect(
						transform.x,
						transform.y,
						radius,
						platform.x,
						platform.y,
						platform.width,
						platform.height
					)
				) {
					hitPlatformEntity = platformEntity;
					const closest = circleRectClosestPoint(
						transform.x,
						transform.y,
						platform.x,
						platform.y,
						platform.width,
						platform.height
					);
					hitImpactX = closest.x;
					hitImpactY = closest.y;
					hasImpact = true;
				}
			});
			if (hitPlatformEntity !== null && hasImpact) {
				toRemove.push(entity);
				if (tuning.platformDestructionEnabled) {
					platformSplits.push({ platformEntity: hitPlatformEntity, impactX: hitImpactX, impactY: hitImpactY });
				}
				return;
			}

			if (projectile.fromEnemy && playerId && playerTransform && playerKinematic) {
				// Enemy projectile (circle) vs player physical collider (AABB)
				const halfW = playerKinematic.halfSize;
				const halfH = playerKinematic.halfHeight ?? playerKinematic.halfSize;
				if (
					circleIntersectsRect(
						transform.x,
						transform.y,
						projectile.size * 0.5,
						playerTransform.x - halfW,
						playerTransform.y - halfH,
						halfW * 2,
						halfH * 2
					)
				) {
					options.applyPlayerDamage(world, tuning.projectilePlayerDamage);
					toRemove.push(entity);
				}
			} else if (!projectile.fromEnemy) {
				let hit = false;
				enemies.forEach((_enemy, enemyEntity) => {
					if (hit) return;
					const enemyTransform = transforms.get(enemyEntity);
					if (!enemyTransform) return;
					const dx = transform.x - enemyTransform.x;
					const dy = transform.y - enemyTransform.y;
					const r = projectile.size * 0.5 + tuning.enemySize * 0.5;
					if (dx * dx + dy * dy <= r * r) {
						const g = graphics.get(enemyEntity);
						if (g) {
							g.parent?.removeChild(g);
							g.destroy();
						}
						const ag = ammoGraphics.get(enemyEntity);
						if (ag) {
							ag.parent?.removeChild(ag);
							ag.destroy();
						}
						ammos.delete(enemyEntity);
						ammoGraphics.delete(enemyEntity);
						world.destroyEntity(enemyEntity);
						hit = true;
						toRemove.push(entity);
					}
				});
			}

			if (projectile.lifetime <= 0) {
				toRemove.push(entity);
			}
		});

		for (const id of toRemove) {
			const g = graphics.get(id);
			if (g) {
				g.parent?.removeChild(g);
				g.destroy();
			}
			world.destroyEntity(id);
		}

		if (platformSplits.length && stage && tuning.platformDestructionEnabled) {
			for (const split of platformSplits) {
				const platform = platforms.get(split.platformEntity);
				if (!platform) continue;
				const leftEdge = platform.x;
				const rightEdge = platform.x + platform.width;
				const gap = tuning.platformSplitGap;
				const cutX = clamp(split.impactX, leftEdge, rightEdge);
				const leftWidth = cutX - leftEdge - gap / 2;
				const rightWidth = rightEdge - cutX - gap / 2;

				options.spawnExplosion(world, transforms, graphics, stage, split.impactX, split.impactY, tuning);

				const g = graphics.get(split.platformEntity);
				if (g) {
					g.parent?.removeChild(g);
					g.destroy();
				}
				world.destroyEntity(split.platformEntity);

				if (leftWidth >= tuning.platformMinPieceWidth) {
					options.spawnPlatform(world, platforms, graphics, stage, leftEdge, platform.y, leftWidth, platform.height);
				}
				if (rightWidth >= tuning.platformMinPieceWidth) {
					options.spawnPlatform(
						world,
						platforms,
						graphics,
						stage,
						cutX + gap / 2,
						platform.y,
						rightWidth,
						platform.height
					);
				}
			}
		}
	};
}


